import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 159808 $
// Purpose     : login/logout parts
// =============================================================================
// ---Third-party imports
import React, { useState, useEffect } from 'react';
import { Icon, Header, Form, Segment, Button, Input, Message, Image } from 'semantic-ui-react';
// ---Framework imports
import { get } from 'ldash';
import { Actions } from 'uix/registries';
import log from 'uix/logging';
import { usePersistent, useActions, useSelector, useCallApi } from 'uix/hooks';
import { HeaderSettingsIcon } from 'uix/components';
import { WebAuthNDiag } from './components';
import connect from 'uix/connect';
import { queryWebauthn, setUser } from './actions';
import { VIcon, vicon } from 'uix/graphics';
import { Sel } from 'uix/registries';
// ---Local imports
import styles from './auth.scss';
import clsx from 'clsx';
// ---Exports
const RecoverForm = ({ username, onClose })=>{
    let i18n = useSelector(Sel.i18n);
    let { callApi: recoverPasswordMail } = useCallApi('user/recoverPasswordMail');
    let [email, setEmail] = useState('');
    let [recoverFailure, setRecoverFailure] = useState('');
    let [mailSuccess, setMailSuccess] = useState(false);
    let sendMail = ()=>{
        setRecoverFailure('');
        recoverPasswordMail({
            login: username,
            email
        }, (data, error)=>{
            setMailSuccess(!!data?.mail_send);
            if (data?.recover_error) {
                setRecoverFailure(data?.recover_error);
            }
        });
    };
    let isValidEmail = (email)=>{
        let emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };
    return /*#__PURE__*/ _jsxs(Form, {
        className: clsx('attached fluid segment', styles.formContainer),
        onSubmit: ()=>sendMail(),
        error: !!recoverFailure,
        success: mailSuccess,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.headerForget,
                style: {},
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        secondary: true,
                        icon: "arrow left",
                        onClick: onClose
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        children: i18n.get('resetpw')
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                style: {
                    margin: '1rem 0 ',
                    textAlign: 'center'
                },
                children: [
                    "Geben Sie die mit ",
                    /*#__PURE__*/ _jsx("b", {
                        children: username
                    }),
                    " verbundene E-Mail Adresse ein:"
                ]
            }),
            /*#__PURE__*/ _jsxs(Input, {
                name: "email",
                value: email,
                iconPosition: "left",
                placeholder: i18n.get('email', 'Email'),
                onChange: (e, { value })=>setEmail(value),
                required: true,
                fluid: true,
                ref: (input)=>input ? input.focus() : null,
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "at"
                    }),
                    /*#__PURE__*/ _jsx("input", {})
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.buttonArea,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs(Message, {
                                size: "mini",
                                error: true,
                                children: [
                                    /*#__PURE__*/ _jsx(Icon, {
                                        name: "exclamation"
                                    }),
                                    recoverFailure
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Message, {
                                size: "mini",
                                success: true,
                                children: "Success"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        disabled: !isValidEmail(email),
                        className: styles.submit,
                        fluid: true,
                        content: "Neues Passwort anfordern"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {})
        ]
    });
};
export const AuthLogin = ()=>{
    let runLogin = useActions(Actions.runLogin);
    let apiurl = useSelector(Sel.apiurl);
    let url = apiurl.href(`/favicon.ico`);
    let urlStr = `${apiurl.baseUrl('').slice(8)}`;
    let [username, setUsername] = usePersistent('username', '');
    let [showPassword, setShowPassword] = usePersistent('showPassword', true);
    let [password, setPassword] = useState('');
    let sel = useSelector((root)=>({
            loginFailure: root.meta.loginFailure,
            user: root.meta.user,
            i18n: root.meta.i18n,
            request: root.meta.request,
            webauthn: root.meta.webauthn,
            email_on: root.config.instance.email.on
        }));
    let webauthnAvailable = sel.webauthn.available && sel.webauthn.available !== 'LOADING';
    let webauthnError = sel.webauthn.state && sel.webauthn.state.startsWith('ERROR_');
    let usePassword = showPassword || webauthnError;
    let noInsufficientPermissionHack = sel.request.classname == 'test' && sel.request.tabname == 'auth';
    let failure = null;
    // show recover form instead of login form
    let [recoverMode, setRecoverMode] = useState(false);
    let onPasswordRecoverClick = null;
    if (sel.email_on) {
        onPasswordRecoverClick = ()=>setRecoverMode(true);
    }
    if (sel.loginFailure) {
        failure = /*#__PURE__*/ _jsxs(Message, {
            className: styles.failure,
            size: "mini",
            error: true,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "exclamation"
                }),
                get(sel.i18n, sel.loginFailure.reason, sel.loginFailure.reason)
            ]
        });
    } else if (sel.user.name && !noInsufficientPermissionHack) {
        // if we have a valid login, assume it's a permission problem
        // we also need to handle i18n manually
        failure = /*#__PURE__*/ _jsxs(Message, {
            className: styles.failure,
            size: "mini",
            error: true,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "exclamation"
                }),
                sel.i18n['msg_insufficient_permission']
            ]
        });
    }
    let passwordToggle = (value)=>{
        setShowPassword(value);
        // auto magic, run non-pw login immediately
        if (!value) {
            runLogin(null);
        }
    };
    let runLoginHandler = ()=>{
        if (password === undefined) {
            password = usePassword ? password : null;
        }
        runLogin({
            username: username,
            password
        });
    };
    let content = /*#__PURE__*/ _jsxs(Form, {
        className: clsx('attached fluid segment', styles.formContainer),
        onSubmit: ()=>runLoginHandler(),
        children: [
            /*#__PURE__*/ _jsx(Image, {
                src: url,
                onError: (e)=>{
                    e.target.onError = null; //prevents looping
                    e.target.src = ''; //fallback image
                },
                style: {
                    height: '3rem'
                }
            }),
            /*#__PURE__*/ _jsxs(Header, {
                className: styles.header,
                textAlign: "center",
                as: "h2",
                children: [
                    get(sel.i18n, 'welcomeBack', 'Welcome back'),
                    "!",
                    /*#__PURE__*/ _jsx(Header.Subheader, {
                        size: "tiny",
                        children: urlStr
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Field, {
                className: styles.inputs,
                children: /*#__PURE__*/ _jsxs(Input, {
                    name: "username",
                    autoComplete: "username",
                    value: username,
                    iconPosition: "left",
                    placeholder: get(sel.i18n, 'username', 'User'),
                    onChange: (e, { value })=>setUsername(value),
                    required: true,
                    ref: (input)=>input && (!username || !usePassword) ? input.focus() : null,
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            name: "user"
                        }),
                        /*#__PURE__*/ _jsx("input", {})
                    ]
                })
            }),
            usePassword ? /*#__PURE__*/ _jsx(PasswordField, {
                username: username,
                isFocused: Boolean(username),
                placeholder: get(sel.i18n, 'password', 'Password'),
                onChange: (password)=>setPassword(password),
                showAlways: showPassword || webauthnError,
                onPasswordRecoverClick: onPasswordRecoverClick
            }) : null,
            !showPassword || webauthnAvailable ? /*#__PURE__*/ _jsxs(Button.Group, {
                fluid: true,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        icon: "keyboard outline",
                        style: {
                            paddingRight: '1em'
                        },
                        positive: usePassword,
                        onClick: ()=>passwordToggle(true)
                    }),
                    /*#__PURE__*/ _jsx(Button.Or, {}),
                    /*#__PURE__*/ _jsx(Button, {
                        positive: !usePassword,
                        icon: {
                            ...vicon('fingerprint'),
                            style: {
                                paddingLeft: '0.3em'
                            }
                        },
                        onClick: ()=>passwordToggle(false)
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(styles.buttonArea, failure && styles.failure),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: failure
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        className: styles.submit,
                        fluid: true,
                        animated: "fade",
                        children: [
                            /*#__PURE__*/ _jsx(Button.Content, {
                                visible: true,
                                children: get(sel.i18n, 'doLoginButtonLabel', 'Log in')
                            }),
                            /*#__PURE__*/ _jsx(Button.Content, {
                                hidden: true,
                                children: /*#__PURE__*/ _jsx(VIcon, {
                                    name: "sign-in"
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
    if (recoverMode) {
        content = /*#__PURE__*/ _jsx(RecoverForm, {
            username: username,
            onClose: ()=>setRecoverMode(false)
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.container,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.authContainer,
            children: content
        })
    });
};
let PasswordField = connect('LoginPasswordField', {
    storeMap: 'meta.webauthn',
    actionMap: {
        queryWebauthn
    }
}, ({ isFocused, placeholder, webauthn, username, queryWebauthn, onChange, showAlways, onPasswordRecoverClick })=>{
    let [password, setPassword] = useState('');
    let i18n = useSelector(Sel.i18n);
    useEffect(()=>{
        if (username) {
            queryWebauthn(username);
        }
    }, [
        username
    ]);
    let the_input = null;
    useEffect(()=>{
        if (isFocused && the_input) {
            the_input.focus();
        }
    }, [
        showAlways
    ]);
    let change = (value)=>{
        onChange(value);
        setPassword(value);
    };
    if (!showAlways && webauthn.available !== true) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Form.Field, {
        style: {
            position: 'relative'
        },
        children: [
            /*#__PURE__*/ _jsxs(Input, {
                name: "password",
                autoComplete: "current-password",
                value: password,
                type: "password",
                iconPosition: "left",
                placeholder: placeholder,
                onChange: (e, { value })=>change(value),
                required: true,
                ref: (input)=>the_input = input,
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "lock"
                    }),
                    /*#__PURE__*/ _jsx("input", {})
                ]
            }),
            onPasswordRecoverClick && /*#__PURE__*/ _jsxs("a", {
                style: {
                    cursor: 'pointer',
                    paddingTop: '.3em',
                    textAlign: 'right',
                    width: '100%',
                    display: 'block',
                    fontSize: '.85em'
                },
                onClick: onPasswordRecoverClick,
                children: [
                    i18n.get('resetpw'),
                    " ?"
                ]
            })
        ]
    });
});
export const WebAuthN = ({})=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Message, {
                negative: true,
                children: /*#__PURE__*/ _jsx("a", {
                    href: "https://github.com/Yubico/python-fido2/issues/52",
                    children: "See: Windows TPM attestation probs, adapt to pywebauthn"
                })
            }),
            /*#__PURE__*/ _jsxs(Segment, {
                children: [
                    /*#__PURE__*/ _jsx(HeaderSettingsIcon, {
                        content: "WebAuthN",
                        subheader: "test registration and login procedure",
                        icon: vicon('fingerprint')
                    }),
                    /*#__PURE__*/ _jsx(WebAuthNDiag, {})
                ]
            })
        ]
    });
